import { Injectable } from '@angular/core';
import {
  CustomTableCell,
  RowAction,
  TableData,
} from '../../components/table/table.interface';

@Injectable()
export class TableFormatService {
  canvas: HTMLCanvasElement | undefined;
  columnDistribution = 'repeat(var(--cols-count), minmax(114px, 1fr))';

  private getTextWidth = (text: string, font: string) => {
    if (this.canvas === undefined) {
      this.canvas = document.createElement('canvas');
    }
    let context = this.canvas.getContext('2d');
    if (context) {
      context.font = font;
      var metrics = context.measureText(text);
      return metrics.width;
    } else {
      return 0;
    }
  };

  calculateHeadWordWidth = (
    head: string,
    isSubCol: boolean = false
  ): number => {
    let minHeadWidth = 0;
    head.split(' ').forEach((word) => {
      let wordWidth = this.getTextWidth(
        word,
        isSubCol ? '15px "Inter", sans-serif' : 'bold 18px "Inter", sans-serif'
      );
      wordWidth += isSubCol ? 20 : 45;
      if (wordWidth > minHeadWidth) {
        minHeadWidth = wordWidth;
      }
    });
    return minHeadWidth;
  };

  resizeColumns = (
    data: TableData,
    canSelect: boolean,
    hasIndexCol: boolean,
    actionCols: RowAction[],
    customCells: CustomTableCell[] = [],
  ) => {
    console.log('Resizing columns...');
    let distribution = '';
    const visibleHeads = data.heads.filter((head) => !head.ignore);
    const columnAverageLength: (
      | { flex: true; length: number; static: false }
      | { flex: false; length: number; static: false }
      | { flex: false; length: string; static: true }
    )[] = [];
    const headsMinLength: number[] = [];

    let colDistributionIndex = 0;

    visibleHeads.forEach((head) => {
      let minHeadWidth = this.calculateHeadWordWidth(head.name);
      const customMinWidth = customCells.find(
        (cell) => cell.column === head.name,
      )?.minWidth;
      if (customMinWidth !== undefined) {
        minHeadWidth = Math.max(minHeadWidth, customMinWidth);
      }

      if (head.type === 'group') {
        let subColsMaxWidth = 0;
        head.groupColumns?.forEach((subCol) => {
          subColsMaxWidth += this.calculateHeadWordWidth(subCol.name, true);
        });
        minHeadWidth = Math.max(minHeadWidth, subColsMaxWidth);
      }

      headsMinLength.push(minHeadWidth);

      // Initialize columnAverageLength with the header width (minHeadWidth)
      if (head.type === 'button' || head.type == 'index' || head.dataType === 'link') {
        columnAverageLength.push({
          flex: false,
          length: head.dataType === 'link' ? '220px' : '114px',
          static: true,
        });
      } else {
        columnAverageLength.push({
          flex: false,
          length: Math.max(minHeadWidth, 114),
          static: false,
        });
      }

      colDistributionIndex++;
    });

    // --- STEP 2: Adjust widths if the table HAS rows (i.e., table is filled) ---
    if (data.rows.length) {
      let colContentIndex = 0;
      visibleHeads.forEach((head) => {
        // Skip columns with static/fixed width (button/index)
        if (head.type === 'button' || head.type == 'index' || head.dataType === 'link') {
          colContentIndex++;
          return;
        }

        // Calculate content width
        let maxWidth = 0;
        for (let row of data.rows) {
          const length = this.getTextWidth(
            String(row.value[head.name]),
            'normal 18px "Inter", sans-serif'
          );
          if (length && length > maxWidth) {
            maxWidth = length;
          }
        }

        const contentWidth = maxWidth ? maxWidth + 32 : 0;

        // Use the maximum of the content width and the initial header/min-width
        const currentMinLength = Number(
          columnAverageLength[colContentIndex].length
        );

        // If content is wider than the current minimum (header/114px)
        if (contentWidth > currentMinLength) {
          (
            columnAverageLength[colContentIndex] as {
              flex: false;
              length: number;
              static: false;
            }
          ).length = contentWidth;
        }

        colContentIndex++;
      });
    }

    // --- STEP 3: Generate the final distribution string ---
    if (columnAverageLength.length > 0) {
      distribution = columnAverageLength
        .map((column, index) => {
          const head = visibleHeads[index];
          // Get the corresponding min header length (since arrays are now aligned)
          const minHeadLength = headsMinLength[index];

          // Handle static button/index types
          if (head && column.static && (head.type === 'button' || head.dataType === 'link')) {
            return head.dataType === 'link'
              ? 'minmax(220px, 1.35fr)'
              : 'minmax(180px, 100%)';
          }

          // For dynamic columns, use the max of content/header/114px
          const minWidth = Math.max(
            Number(column.length),
            minHeadLength || 114
          );

          return `minmax(${minWidth}px, 1fr)`; // Use 1fr (fraction) for flexibility
        })
        .join(' ');
    } else {
      // Fallback if there are heads but all are ignored/empty, or logic error
      distribution = `repeat(var(--cols-count), minmax(114px, 1fr))`;
    }

    // --- STEP 4: Add static columns (Index, Select, Actions) ---
    if (hasIndexCol) {
      distribution = '80px ' + distribution;
    }
    if (canSelect) {
      distribution = '40px ' + distribution;
    }

    distribution += ' 140px'.repeat(actionCols.length);

    this.columnDistribution = distribution.trim();
  };

  verticalSpan = (data: TableData, rowIndex: number, colName: number) => {
    let span = 1;

    const value = data.rows[rowIndex].value[colName];
    // const value = this.currentPageData[rowIndex][colIndex].value.content;

    const nextRow = data.rows[rowIndex + 1];
    const prevRow = data.rows[rowIndex - 1];

    if (prevRow && nextRow) {
      if (
        nextRow.value[colName] === value &&
        prevRow.value[colName] !== value
      ) {
        span = data.rows
          .slice(rowIndex)
          .map((row) => row.value[colName])
          .reduce((acc, current, i, arr) => {
            if (current != value) {
              arr.splice(1);
              return acc;
            } else {
              return acc + 1;
            }
          }, 0);
      } else if (
        nextRow.value[colName] === value ||
        prevRow.value[colName] === value
      ) {
        span = 0;
      }
    } else if (prevRow) {
      if (prevRow.value[colName] === value) {
        span = 0;
      }
    } else if (nextRow) {
      if (nextRow.value[colName] === value) {
        span = data.rows
          .slice(rowIndex)
          .map((row) => row.value[colName])
          .reduce((acc, current, i, arr) => {
            if (current != value) {
              arr.splice(1);
              return acc;
            } else {
              return acc + 1;
            }
          }, 0);
      }
    }

    return span;
  };
}
